import { useCallback, useEffect, useState } from 'react';

// Results grid display settings. Held in localStorage under one key, so they
// apply to every loan in the browser and survive relaunching Partner UI.

export interface GridPreferences {
  verticalLines: boolean;
  horizontalLines: boolean;
  rowBanding: boolean;
  displaySearchCriteria: boolean;
}

export const DEFAULT_PREFERENCES: GridPreferences = {
  verticalLines: true,
  horizontalLines: true,
  rowBanding: true,
  displaySearchCriteria: false,
};

export const STORAGE_KEY = 'ppe.resultsGrid.prefs.v1';

// Reads and writes fall back to this copy when localStorage throws.
let cache: GridPreferences | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((fn) => fn());

// Stored values merge over the defaults, anything unrecognised is dropped.
export const readPreferences = (): GridPreferences => {
  if (cache) return cache;

  let stored: Partial<GridPreferences> = {};
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') stored = parsed;
    }
  } catch {
    stored = {};
  }

  const merged = { ...DEFAULT_PREFERENCES };
  (Object.keys(DEFAULT_PREFERENCES) as Array<keyof GridPreferences>).forEach(
    (field) => {
      if (typeof stored[field] === 'boolean') {
        merged[field] = stored[field] as boolean;
      }
    },
  );
  cache = merged;
  return merged;
};

export const writePreferences = (next: GridPreferences) => {
  cache = next;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // in memory only for the rest of the session
  }
  notify();
};

/** Clears the module cache between tests. */
export const resetPreferencesCache = () => {
  cache = null;
};

export const useGridPreferences = () => {
  const [preferences, setPreferences] = useState(readPreferences);

  useEffect(() => {
    const sync = () => setPreferences(readPreferences());
    sync();
    listeners.add(sync);
    return () => {
      listeners.delete(sync);
    };
  }, []);

  // Takes the menu's full selection map.
  const applySelection = useCallback((selection: Record<string, unknown>) => {
    const next = { ...DEFAULT_PREFERENCES };
    (Object.keys(DEFAULT_PREFERENCES) as Array<keyof GridPreferences>).forEach(
      (field) => {
        next[field] = Boolean(selection[field]);
      },
    );
    writePreferences(next);
  }, []);

  return { preferences, applySelection };
};
